/**
 * Boxes as printed on ICS 225-CG (Jan 2026), Incident Personnel Performance
 * Rating.
 *
 * Unlike the rest of the Coast Guard set this sheet is about a person rather
 * than an operational period, so box 2 is the incident name and box 4 its
 * location — the reverse of the usual 1/2 ordering.
 */
export const ICS225_BOX_LABELS = {
  ratedName: '1. Name of Rated Individual:',
  incidentName: '2. Incident Name:',
  contact: '3. Rated Individual’s Phone Number/Email:',
  incidentLocation: '4. Location of Incident:',
  positionAssigned: '5. Position Assigned:',
  datesOfAssignment: '6. Dates of Assignment:',
  incidentStartDate: '7. Incident Start Date:',
  incidentType: '8. Incident Type:',
  incidentKind: '9. Incident Kind:',
  evaluation: '10. EVALUATION',
  accomplishments: '11. Responsibilities and Notable Accomplishments:',
  ratedPersonSignature:
    '12. Rated Person (Signature). This rating has been discussed with me:',
  ratedPersonDate: '13. Date:',
  supervisorPosition: '14. Supervisor Position:',
  supervisorName: '15. Supervisor: (Printed Name)',
  supervisorSignature: '16. Supervisor: (Signature)',
  supervisorDate: '17. Date:',
} as const;

/**
 * Box 10's rating factors, lettered A to L as the sheet letters them.
 *
 * Keys are stored in the `ratings` JSONB column and are what the exported PDF
 * ticks, so renaming one silently drops the stored rating.
 */
export const ICS225_RATING_FACTORS = [
  {
    key: 'icsFamiliarity',
    letter: 'A',
    label: 'ICS Familiarity',
    standard:
      'Understands & properly employs NIMS ICS principles & guidance; follows ICS procedures, terminology, organizational structure & chain of command.',
  },
  {
    key: 'positionalCompetence',
    letter: 'B',
    label: 'Positional Competence',
    standard:
      'Knowledgeable of position roles & responsibilities; assumed position & took appropriate action; produced desirable results; helped achieve incident objectives.',
  },
  {
    key: 'judgementAndDecisionMaking',
    letter: 'C',
    label: 'Judgement & Decision-making',
    standard:
      'Makes informed, accurate decisions; ensured legal & ethical compliance; understands socio-economic, political, and cultural consideration; use pertinent analysis to make recommendations/set priorities.',
  },
  {
    key: 'situationalAwareness',
    letter: 'D',
    label: 'Situational Awareness',
    standard:
      'Able to accurately gather, apply, assess, & disseminate situational information.',
  },
  {
    key: 'collaborationAndCoordination',
    letter: 'E',
    label: 'Collaboration & Coordination',
    standard:
      'Establishes inclusive, effective, working relationships with personnel & stakeholders; coordinates well with others; able to gain concurrence through collaboration.',
  },
  {
    key: 'adaptability',
    letter: 'F',
    label: 'Adaptability',
    standard:
      'Modifies actions/efforts as necessary; adjusts well to changing incident complexity.',
  },
  {
    key: 'leadershipAndSupervision',
    letter: 'G',
    label: 'Leadership & Supervision',
    standard:
      'Provides support; model leadership values/principles; effectively leads others in crisis; properly establishes & monitors assignments & performance expectations.',
  },
  {
    key: 'communication',
    letter: 'H',
    label: 'Communication',
    standard:
      'Effectively disseminates & processes information; provides clear/concise messaging; able to express priorities objectives, operations & expectations.',
  },
  {
    key: 'resourcefulness',
    letter: 'I',
    label: 'Resourcefulness',
    standard:
      'Employs the right tools & resources; use networks to locate resources; use initiative to overcome challenges.',
  },
  {
    key: 'unityOfEffort',
    letter: 'J',
    label: 'Unity of Effort',
    standard: 'Emphasizes teamwork to achieve objectives.',
  },
  {
    key: 'safetyWellbeingAndRiskManagement',
    letter: 'K',
    label: 'Safety, Well-being & Risk Management',
    standard:
      'Anticipates, recognizes & mitigates unsafe situations; consistently ensures personnel safety/well-being.',
  },
  {
    key: 'overallPerformance',
    letter: 'L',
    label: 'Overall Performance in Assigned Position',
    standard:
      'Satisfactorily performs standard position-specific responsibilities according to the USCG Incident Management Handbook (CG-IMH).',
  },
] as const;

export type Ics225RatingFactorKey =
  (typeof ICS225_RATING_FACTORS)[number]['key'];

/** The three measured standards box 10 offers per factor. */
export const ICS225_MEASURED_STANDARDS = [
  'Failed',
  'Met',
  'Exceeded',
] as const;

export type Ics225MeasuredStandard =
  (typeof ICS225_MEASURED_STANDARDS)[number];
